import { CLOSE_DAY } from './config';
import { round2 } from './math';
import type { NutrientKey, NutrientTargets, NutrientValues } from './types';

export interface Deficit {
  key: NutrientKey;
  /** Lo que falta, en la unidad del nutriente. Siempre > 0. */
  missing: number;
  /** Cuánto importa cubrirlo, relativo a los demás. */
  weight: number;
}

export interface CloseDayCandidate {
  id: string;
  name: string;
  per100g: NutrientValues;
  tags: string[];
  /** true si el usuario ya suele comerlo. */
  familiar: boolean;
  /**
   * Porción máxima realista, en gramos. Sin esto el motor cuadra los números
   * con 300 g de chalotas, que nadie se come. Si no se indica, se usa el tope
   * general de la configuración.
   */
  maxGrams?: number;
}

export interface CloseDayInput {
  deficits: readonly Deficit[];
  candidates: readonly CloseDayCandidate[];
  /** kcal que quedan para llegar al objetivo del día. */
  remainingKcal: number;
  upperLimits: NutrientTargets;
  /** Lo ya consumido hoy, para no pasarse de los topes. */
  currentTotals: NutrientValues;
  /** Etiquetas prohibidas: alergias, vegetarianismo, cosas que no come. */
  excludeTags?: readonly string[];
}

export interface CloseDayItem {
  candidate: CloseDayCandidate;
  grams: number;
  kcal: number;
}

export interface CloseDayResult {
  items: CloseDayItem[];
  /** Fracción ponderada de los déficits que cubre la propuesta, de 0 a 1. */
  covered: number;
  /** true cuando no se ha podido cubrir todo con las kcal disponibles. */
  partial: boolean;
  totalKcal: number;
}

/**
 * Cierre de dieta (SPEC 7.1).
 *
 * Busca de 1 a 3 alimentos, en porciones realistas, que tapen la mayor parte
 * de los déficits SIN pasarse de las calorías que quedan ni de los límites
 * superiores. El techo de calorías no se negocia: proponer 400 kcal a quien le
 * quedan 200 convierte el consejo en un problema.
 *
 * La búsqueda es voraz con reinicios: se prueba cada uno de los mejores
 * candidatos como primer alimento y se completa greedy hasta 3. No busca el
 * óptimo global —sería exponencial— pero es determinista y suficiente, y sobre
 * todo nunca se salta las restricciones duras.
 */
export function closeDay(input: CloseDayInput): CloseDayResult | null {
  const deficits = input.deficits.filter((d) => d.missing > 0 && d.weight > 0);
  if (deficits.length === 0) return null;

  const budget = input.remainingKcal * (1 + CLOSE_DAY.kcalTolerance);
  if (!(input.remainingKcal > 0)) return null;

  const excluded = new Set(input.excludeTags ?? []);
  const pool = input.candidates
    .filter((c) => !c.tags.some((tag) => excluded.has(tag)))
    .filter((c) => (c.per100g.kcal ?? 0) > 0)
    .slice(0, CLOSE_DAY.maxCandidates);

  if (pool.length === 0) return null;

  const totalWeight = deficits.reduce((sum, d) => sum + d.weight, 0);

  // Primero, la mejor porción individual de cada candidato, para ordenar.
  const ranked = [...pool]
    .map((candidate) => ({
      candidate,
      best: bestPortion(candidate, deficits, budget, input, []),
    }))
    .filter((r) => r.best != null)
    .sort((a, b) => {
      const diff = b.best!.score - a.best!.score;
      // Desempate estable por nombre: mismo dato, misma respuesta.
      return diff !== 0 ? diff : a.candidate.id.localeCompare(b.candidate.id);
    });

  if (ranked.length === 0) return null;

  let best: CloseDayResult | null = null;

  // Reinicios: cada uno de los mejores como primer alimento.
  for (const seed of ranked.slice(0, 12)) {
    const items: CloseDayItem[] = [
      { candidate: seed.candidate, grams: seed.best!.grams, kcal: seed.best!.kcal },
    ];

    while (items.length < CLOSE_DAY.maxFoods) {
      const used = items.reduce((sum, i) => sum + i.kcal, 0);
      const left = budget - used;
      if (left <= 0) break;

      let pick: {
        candidate: CloseDayCandidate;
        grams: number;
        kcal: number;
        score: number;
      } | null = null;

      for (const { candidate } of ranked) {
        if (items.some((i) => i.candidate.id === candidate.id)) continue;
        const portion = bestPortion(candidate, deficits, left, input, items);
        if (!portion || portion.score <= 0) continue;
        if (
          pick == null ||
          portion.score > pick.score ||
          (portion.score === pick.score && candidate.id.localeCompare(pick.candidate.id) < 0)
        ) {
          pick = { candidate, grams: portion.grams, kcal: portion.kcal, score: portion.score };
        }
      }

      if (!pick) break;
      items.push({ candidate: pick.candidate, grams: pick.grams, kcal: pick.kcal });
    }

    const covered = coverageOf(items, deficits, totalWeight);
    const totalKcal = round2(items.reduce((sum, i) => sum + i.kcal, 0));
    const candidate: CloseDayResult = {
      items,
      covered: round2(covered),
      partial: covered < 1 - CLOSE_DAY.fullCoverageEpsilon,
      totalKcal,
    };

    if (best == null || isBetter(candidate, best)) best = candidate;
  }

  return best;
}

/** Mejor que otra si cubre más; a igualdad, la que gasta menos calorías. */
function isBetter(a: CloseDayResult, b: CloseDayResult): boolean {
  if (a.covered !== b.covered) return a.covered > b.covered;
  if (a.totalKcal !== b.totalKcal) return a.totalKcal < b.totalKcal;
  return a.items.length < b.items.length;
}

interface Portion {
  grams: number;
  kcal: number;
  score: number;
}

/**
 * Mejor cantidad de un alimento, en pasos de 25 g, dentro del presupuesto y sin
 * romper ningún límite superior.
 */
function bestPortion(
  candidate: CloseDayCandidate,
  deficits: readonly Deficit[],
  kcalBudget: number,
  input: CloseDayInput,
  alreadyChosen: readonly CloseDayItem[],
): Portion | null {
  const kcalPer100 = candidate.per100g.kcal ?? 0;
  if (kcalPer100 <= 0) return null;

  let best: Portion | null = null;

  const cap = Math.min(candidate.maxGrams ?? CLOSE_DAY.maxGrams, CLOSE_DAY.maxGrams);

  for (let grams = CLOSE_DAY.minGrams; grams <= cap; grams += CLOSE_DAY.gramStep) {
    const kcal = (kcalPer100 * grams) / 100;
    if (kcal > kcalBudget) break;
    if (exceedsLimits(candidate, grams, input, alreadyChosen)) break;

    const score = gainOf(candidate, grams, deficits, alreadyChosen, candidate.familiar);
    if (score <= 0) continue;

    // A igualdad de utilidad gana la porción más pequeña: comer de más sin
    // ganar nada no es un consejo, es relleno.
    if (best == null || score > best.score) best = { grams, kcal: round2(kcal), score };
  }

  return best;
}

/**
 * ¿Esta porción se pasaría de algún tope (sodio, saturadas)?
 *
 * Si el día YA va por encima de un tope, la propuesta entera solo puede añadir
 * trazas (CLOSE_DAY.overLimitAllowance): antes se rechazaba todo, hasta una
 * fruta, y "Cerrar el día" desaparecía sin explicación (SPEC 7.1.5 pide la
 * mejor propuesta posible). Y en ese caso un valor desconocido no se da por
 * cero: podría añadir mucho.
 */
function exceedsLimits(
  candidate: CloseDayCandidate,
  grams: number,
  input: CloseDayInput,
  alreadyChosen: readonly CloseDayItem[],
): boolean {
  for (const [key, limit] of Object.entries(input.upperLimits) as [NutrientKey, number][]) {
    const current = input.currentTotals[key] ?? 0;
    const fromChosen = alreadyChosen.reduce(
      (sum, item) => sum + ((item.candidate.per100g[key] ?? 0) * item.grams) / 100,
      0,
    );
    const value = candidate.per100g[key];
    const added = ((value ?? 0) * grams) / 100;

    if (current >= limit) {
      if (value == null) return true;
      if (fromChosen + added > limit * CLOSE_DAY.overLimitAllowance) return true;
    } else if (current + fromChosen + added > limit) {
      return true;
    }
  }
  return false;
}

/**
 * Utilidad de añadir esta porción: cuánto déficit ponderado tapa, contando solo
 * lo que aún falta después de lo ya elegido. Lo que sobra de un nutriente no
 * suma: cubrir el hierro tres veces no vale por tres.
 */
function gainOf(
  candidate: CloseDayCandidate,
  grams: number,
  deficits: readonly Deficit[],
  alreadyChosen: readonly CloseDayItem[],
  familiar: boolean,
): number {
  let gain = 0;

  for (const deficit of deficits) {
    const already = alreadyChosen.reduce(
      (sum, item) => sum + ((item.candidate.per100g[deficit.key] ?? 0) * item.grams) / 100,
      0,
    );
    const stillMissing = Math.max(deficit.missing - already, 0);
    if (stillMissing <= 0) continue;

    const provided = ((candidate.per100g[deficit.key] ?? 0) * grams) / 100;
    if (provided <= 0) continue;

    gain += (Math.min(provided, stillMissing) / deficit.missing) * deficit.weight;
  }

  // Empujón a lo que ya come: un consejo que encaja con sus hábitos se sigue.
  return gain * (familiar ? 1 + CLOSE_DAY.familiarityBonus : 1);
}

function coverageOf(
  items: readonly CloseDayItem[],
  deficits: readonly Deficit[],
  totalWeight: number,
): number {
  let covered = 0;

  for (const deficit of deficits) {
    const provided = items.reduce(
      (sum, item) => sum + ((item.candidate.per100g[deficit.key] ?? 0) * item.grams) / 100,
      0,
    );
    covered += (Math.min(provided, deficit.missing) / deficit.missing) * deficit.weight;
  }

  return totalWeight === 0 ? 0 : covered / totalWeight;
}
